/**
 * Form model for Edit project: turning a project into form values, checking
 * them, and working out which fields actually changed so a save sends only
 * those.
 */
import type { ProjectDetails, ProjectUpdate } from '../services/ProjectService';

export interface ProjectFormValues {
  name: string;
  description: string;
  clientId: string;
  dateStart: string;
  dateEnd: string;
  eventDate: string;
  city: string;
  country: string;
}

export const EMPTY_PROJECT_FORM: ProjectFormValues = {
  name: '', description: '', clientId: '', dateStart: '', dateEnd: '', eventDate: '', city: '', country: '',
};

/** `2026-11-28T00:00:00Z` or `2026-11-28` becomes `2026-11-28`; anything else is blank. */
export function toDateInput(v: string | null | undefined): string {
  const m = typeof v === 'string' ? /^(\d{4}-\d{2}-\d{2})/.exec(v) : null;
  return m ? m[1] : '';
}

export function toFormValues(p: Partial<ProjectDetails> | null | undefined): ProjectFormValues {
  return {
    name: p?.name ?? '',
    description: p?.description ?? '',
    clientId: p?.client_id ?? '',
    dateStart: toDateInput(p?.date_start),
    dateEnd: toDateInput(p?.date_end),
    eventDate: toDateInput(p?.event_date),
    city: p?.location_city ?? '',
    country: p?.location_country ?? '',
  };
}

export type ProjectFormErrors = Partial<Record<keyof ProjectUpdate, string>>;

/** Field errors keyed like the API's own (name, date_end, …); empty when the form is fine. */
export function validateProjectForm(v: ProjectFormValues): ProjectFormErrors {
  const errors: ProjectFormErrors = {};
  if (!v.name.trim()) errors.name = 'Name is required.';
  if (v.dateStart && v.dateEnd && v.dateEnd < v.dateStart) {
    errors.date_end = 'End date cannot be before the start date.';
  }
  return errors;
}

const FIELDS: { form: keyof ProjectFormValues; api: keyof ProjectUpdate }[] = [
  { form: 'name', api: 'name' },
  { form: 'description', api: 'description' },
  { form: 'clientId', api: 'client_id' },
  { form: 'dateStart', api: 'date_start' },
  { form: 'dateEnd', api: 'date_end' },
  { form: 'eventDate', api: 'event_date' },
  { form: 'city', api: 'location_city' },
  { form: 'country', api: 'location_country' },
];

const clean = (_key: keyof ProjectFormValues, v: string) => v.trim();

/** Only the fields whose value differs from `initial`; a cleared optional field is sent as null. */
export function changedFields(initial: ProjectFormValues, current: ProjectFormValues): ProjectUpdate {
  const out: Record<string, string | null> = {};
  for (const { form, api } of FIELDS) {
    const before = clean(form, initial[form]);
    const after = clean(form, current[form]);
    if (before === after) continue;
    out[api] = after === '' && form !== 'name' ? null : after;
  }
  return out as ProjectUpdate;
}
